import React, { useCallback, useContext, useEffect, useState, useRef } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { GALLERY_VIEW_ASPECT_RATIO, GALLERY_VIEW_MARGIN } from '../../constants';
import { IconButton, Pagination } from '@mui/material';
import { styled } from '@mui/material/styles';
import Participant from '../Participant/Participant';
import useGalleryViewLayout from '../../hooks/useGalleryViewLayout/useGalleryViewLayout';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useParticipantsContext from '../../hooks/useParticipantsContext/useParticipantsContext';
import { usePagination } from './usePagination/usePagination';
import useDominantSpeaker from '../../hooks/useDominantSpeaker/useDominantSpeaker';
import { useAppState } from '../../state';
import { SocketContext } from '../../utils/socket';
import MeetingLeaveDialog from '../MeetingLeaveDialog/MeetingLeaveDialog';
import { RemoteParticipant } from 'twilio-video';

const CONTAINER_GUTTER = '50px';

// Timeout constants (in milliseconds)
// const ALONE_TIMEOUT_MS = 30000; // 5 minutes before showing dialog when alone
// const AUTO_LEAVE_TIMEOUT_MS = 10000; // 60 seconds to respond before auto-disconnect
const ALONE_TIMEOUT_MS = 300000; // 5 minutes before showing dialog when alone
const AUTO_LEAVE_TIMEOUT_MS = 60000; // 60 seconds to respond before auto-disconnect

const Container = styled('div')(({ theme }) => ({
  background: theme.galleryViewBackgroundColor,
  position: 'relative',
  gridArea: '1 / 1 / 2 / 3',
}));

const ParticipantContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  display: 'flex',
  top: CONTAINER_GUTTER,
  right: CONTAINER_GUTTER,
  bottom: `calc(${CONTAINER_GUTTER} + ${theme.footerHeight}px)`,
  left: CONTAINER_GUTTER,
  margin: '0 auto',
  alignContent: 'center',
  flexWrap: 'wrap',
  justifyContent: 'center',
}));

const ButtonContainer = styled('div')({
  position: 'absolute',
  top: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ButtonContainerLeft = styled(ButtonContainer)({
  right: `calc(100% - ${CONTAINER_GUTTER})`,
  left: 0,
});

const ButtonContainerRight = styled(ButtonContainer)({
  right: 0,
  left: `calc(100% - ${CONTAINER_GUTTER})`,
});

const StyledPagination = styled(Pagination)({
  '& .MuiPaginationItem-root': {
    color: 'white',
  },
});

const PaginationButton = styled(IconButton)({
  color: 'black',
  background: 'rgba(255, 255, 255, 0.8)',
  width: '40px',
  height: '40px',
  '&:hover': {
    background: 'rgba(255, 255, 255)',
  },
});

const PaginationContainer = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: `calc(100% - ${CONTAINER_GUTTER} - ${theme.footerHeight}px)`,
  right: 0,
  bottom: `${theme.footerHeight}px`,
  left: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

export function GalleryView() {
  const { maxGalleryViewParticipants, signOut } = useAppState();
  const { room } = useVideoContext();
  const socket = useContext(SocketContext);
  const [open, setOpen] = useState(false);
  const { galleryViewParticipants } = useParticipantsContext();
  const { localTracks } = useVideoContext();
  const dominantSpeaker = useDominantSpeaker(true);
  
  // Use refs to track timeouts so they persist across renders
  const aloneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLeaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { paginatedParticipants, setCurrentPage, currentPage, totalPages } = usePagination([
    room!.localParticipant,
    ...galleryViewParticipants,
  ]);

  const galleryViewLayoutParticipantCount =
    currentPage === 1 ? paginatedParticipants.length : maxGalleryViewParticipants;
  const { participantVideoWidth, containerRef } = useGalleryViewLayout(galleryViewLayoutParticipantCount);

  const participantWidth = `${participantVideoWidth}px`;
  const participantHeight = `${Math.floor(participantVideoWidth * GALLERY_VIEW_ASPECT_RATIO)}px`;

  const handleSignOut = useCallback(() => {
    localTracks.forEach(track => track.stop());
    signOut?.();
  }, [localTracks, signOut]);

  const clearAllTimeouts = useCallback(() => {
    if (aloneTimeoutRef.current) {
      clearTimeout(aloneTimeoutRef.current);
      aloneTimeoutRef.current = null;
    }
    if (autoLeaveTimeoutRef.current) {
      clearTimeout(autoLeaveTimeoutRef.current);
      autoLeaveTimeoutRef.current = null;
    }
  }, []);

  // Helper to send leave reason to API before disconnecting
  const sendLeaveReason = useCallback(async (reason: 'manual' | 'inactivity_timeout' | 'network_error') => {
    const participantSid = room?.localParticipant?.sid;
    if (!participantSid) return;
    
    const api_key = window.sessionStorage.getItem('m_key');
    const m_key = api_key ? api_key : '';
    
    try {
      console.log('[GalleryView] Sending leave_reason:', reason, 'for participant:', participantSid);
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/internal/room/participant-time-update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': m_key,
        },
        body: JSON.stringify({
          participantSid,
          leave_time: new Date().toISOString().slice(0, 19).replace('T', ' '),
          leave_reason: reason,
        }),
      });
    } catch (error) {
      console.error('[GalleryView] Failed to send leave_reason:', error);
    }
  }, [room]);

  // Check if user is alone and start timeout if so
  const checkIfAloneAndStartTimeout = useCallback(() => {
    if (!room) return;
    
    const remoteParticipants = Array.from(room.participants.values());
    const isAlone = remoteParticipants.length === 0;
    
    console.log('[GalleryView] Checking alone status:', { 
      isAlone, 
      remoteParticipantCount: remoteParticipants.length,
      localParticipantSid: room.localParticipant?.sid 
    });
    
    if (isAlone && room.localParticipant?.sid) {
      // Only start timeout if not already running
      if (!aloneTimeoutRef.current) {
        console.log('[GalleryView] User is alone, starting 5-minute timeout');
        aloneTimeoutRef.current = setTimeout(() => {
          // Double-check still alone when timeout fires
          const currentRemoteParticipants = Array.from(room.participants.values());
          if (currentRemoteParticipants.length === 0) {
            console.log('[GalleryView] Still alone after timeout, showing dialog');
            setOpen(true);
          } else {
            console.log('[GalleryView] No longer alone when timeout fired, cancelling');
            aloneTimeoutRef.current = null;
          }
        }, ALONE_TIMEOUT_MS);
      }
    } else {
      // Not alone - clear any pending timeout and close dialog
      if (aloneTimeoutRef.current) {
        console.log('[GalleryView] No longer alone, clearing timeout');
        clearTimeout(aloneTimeoutRef.current);
        aloneTimeoutRef.current = null;
      }
      setOpen(false);
    }
  }, [room]);

  // Handle user choosing to leave (via dialog button)
  const handleClose = useCallback(async () => {
    clearAllTimeouts();
    setOpen(false);
    sendLeaveReason('manual');
    room?.disconnect();
    handleSignOut();
  }, [clearAllTimeouts, room, handleSignOut, sendLeaveReason]);

  // Handle user choosing to stay in meeting
  const handleStayMeeting = useCallback(() => {
    console.log('[GalleryView] User chose to stay, resetting timeouts');
    clearAllTimeouts();
    setOpen(false);
    // Restart the alone check (will start new timeout if still alone)
    checkIfAloneAndStartTimeout();
  }, [clearAllTimeouts, checkIfAloneAndStartTimeout]);

  // Event-driven participant tracking
  useEffect(() => {
    if (!room) return;

    const handleParticipantConnected = (participant: RemoteParticipant) => {
      console.log('[GalleryView] Participant connected:', participant.sid);
      // Immediately cancel any pending "alone" timeout
      if (aloneTimeoutRef.current) {
        clearTimeout(aloneTimeoutRef.current);
        aloneTimeoutRef.current = null;
      }
      // Also cancel auto-leave timeout and close dialog
      if (autoLeaveTimeoutRef.current) {
        clearTimeout(autoLeaveTimeoutRef.current);
        autoLeaveTimeoutRef.current = null;
      }
      setOpen(false);
    };

    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      console.log('[GalleryView] Participant disconnected:', participant.sid);
      // Check if now alone after a short delay to let participant list update
      setTimeout(() => {
        checkIfAloneAndStartTimeout();
      }, 500);
    };

    // Register event handlers
    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);

    // Initial check when component mounts or room changes
    checkIfAloneAndStartTimeout();

    // Cleanup
    return () => {
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
      clearAllTimeouts();
    };
  }, [room, checkIfAloneAndStartTimeout, clearAllTimeouts]);

  // Handle socket leaveMeeting events (from other sources like server-initiated kicks)
  useEffect(() => {
    const handleSocketLeaveMeeting = (data: any) => {
      console.log('[GalleryView] Socket leaveMeeting received:', data);
      // Let the participantDisconnected event handle this naturally
      // Just trigger a recheck after a delay
      setTimeout(() => {
        checkIfAloneAndStartTimeout();
      }, 1000);
    };

    socket.on('leaveMeeting', handleSocketLeaveMeeting);
    
    return () => {
      socket.off('leaveMeeting', handleSocketLeaveMeeting);
    };
  }, [socket, checkIfAloneAndStartTimeout]);

  // Auto-leave timeout when dialog is open
  useEffect(() => {
    if (open) {
      console.log('[GalleryView] Dialog opened, starting 60-second auto-leave timer');
      autoLeaveTimeoutRef.current = setTimeout(async () => {
        // Double-check dialog is still open and user is still alone
        const remoteParticipants = room ? Array.from(room.participants.values()) : [];
        if (remoteParticipants.length === 0) {
          console.log('[GalleryView] Auto-leave timer fired, disconnecting due to inactivity');
          await sendLeaveReason('inactivity_timeout');
          setOpen(false);
          room?.disconnect();
          handleSignOut();
        } else {
          console.log('[GalleryView] Auto-leave cancelled - participants joined');
          setOpen(false);
        }
      }, AUTO_LEAVE_TIMEOUT_MS);
    } else {
      // Dialog closed - clear auto-leave timeout
      if (autoLeaveTimeoutRef.current) {
        clearTimeout(autoLeaveTimeoutRef.current);
        autoLeaveTimeoutRef.current = null;
      }
    }

    return () => {
      if (autoLeaveTimeoutRef.current) {
        clearTimeout(autoLeaveTimeoutRef.current);
        autoLeaveTimeoutRef.current = null;
      }
    };
  }, [open, room, handleSignOut]);

  return (
    <Container>
      <ButtonContainerLeft>
        {!(currentPage === 1) && (
          <PaginationButton onClick={() => setCurrentPage(page => page - 1)}>
            <ArrowBack />
          </PaginationButton>
        )}
      </ButtonContainerLeft>
      <ButtonContainerRight>
        {!(currentPage === totalPages) && (
          <PaginationButton onClick={() => setCurrentPage(page => page + 1)}>
            <ArrowForward />
          </PaginationButton>
        )}
      </ButtonContainerRight>
      <PaginationContainer>
        {totalPages > 1 && (
          <StyledPagination
            page={currentPage}
            count={totalPages}
            onChange={(_, value) => setCurrentPage(value)}
            shape="rounded"
            color="primary"
            size="small"
            hidePrevButton
            hideNextButton
          />
        )}
      </PaginationContainer>
      <ParticipantContainer ref={containerRef}>
        {paginatedParticipants.map(participant => (
          <div
            key={participant.sid}
            style={{ width: participantWidth, height: participantHeight, margin: GALLERY_VIEW_MARGIN }}
          >
            <Participant
              participant={participant}
              isLocalParticipant={participant === room!.localParticipant}
              isDominantSpeaker={participant === dominantSpeaker}
            />
          </div>
        ))}
      </ParticipantContainer>
      <MeetingLeaveDialog open={open} handleClose={handleClose} handleStayMeeting={handleStayMeeting} />
    </Container>
  );
}